import {
  ICCOrderTableReport,
  ICCOrderAddOrderFormValues,
  ICCOrderbookSearchParams,
  ICCOrderbooksResp,
  ICCOrderEditValues,
  ICCOrderRules,
  ICCOrderSearchParams,
  IGetCCOrder,
  IUpdateCriterionDTO,
  ICCFileUploadResp,
  ICCFileUploadOrderRow,
  ICCOrderCriterionTypes,
  ICCOrderImbalanceConfig,
  ICCOrderImbalanceRule,
  CoveredCallSendPriorityMode,
  ICoveredCallManualPriorityItem,
  ICoveredCallSendPriorityAccount,
  ICoveredCallSendPrioritySearchParams,
  ICoveredCallSendPriorityTableRow,
  ICoveredCallSendPriorityView,
} from "src/Interfaces/ccOrder";
import { Packet } from "src/Interfaces/commonDtos";
import {
  deleteRequest,
  getRequest,
  getTable,
  postRequest,
  putRequest,
} from "./requestTemplates";
import { ErrorTableReport } from "src/Interfaces/orderInterfaces";
import { Report } from "src/Interfaces/TableInterfaces";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { AxiosRequestConfig } from "axios";
import { User } from "src/Interfaces/connections";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { IShekarOrderMetricEventsResponse } from "src/Interfaces/shekarMetricEvents";
import { DeltaHedgeOrderType } from "src/Interfaces/deltaHedgeOrder";

export const addCCOrder = (
  formValues: ICCOrderAddOrderFormValues,
): Promise<Packet<boolean>> =>
  postRequest("/v1/coveredCallOrder/Add", formValues);

export const getCCOrderTable = (
  searchParam: ICCOrderSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<ICCOrderTableReport>> =>
  getTable<ICCOrderSearchParams, ICCOrderTableReport>(
    "/v1/coveredCallOrder/OrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getCCAggregateOrderTable = (
  searchParam: ICCOrderSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<ICCOrderTableReport>> =>
  getTable<ICCOrderSearchParams, ICCOrderTableReport>(
    "/v1/CoveredCallOrder/AggregatorOrderTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const addCCBulkOrder = (
  orderRows: Array<ICCFileUploadOrderRow> | null,
): Promise<Report<ErrorTableReport>> =>
  postRequest("/v1/coveredCallOrder/addBulkOrder", orderRows);

export const uploadCCOrderFile = (
  searchParam: FormData | null,
): Promise<Report<ICCFileUploadResp>> =>
  getTable<FormData | null, ICCFileUploadResp>(
    "/v1/coveredCallOrder/uploadOrderFile",
    searchParam,
    0,
    50,
  );

export const CCOrderTablePause = (
  ids: number[],
  IsConditionId: boolean = false,
): Promise<Packet<boolean>> =>
  putRequest("/v1/coveredCallOrder/orderTablePause", { ids, IsConditionId });

export const CCOrderTablePlay = (
  ids: number[],
  IsConditionId: boolean = false,
): Promise<Packet<boolean>> =>
  putRequest("/v1/coveredCallOrder/orderTablePlay", { ids, IsConditionId });

export const deleteCCOrders = (
  ids: number[],
  IsConditionId: boolean = false,
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/coveredCallOrder/deleteOrders`, { ids, IsConditionId });

export const deleteCCOpenOrders = (
  ids: number[],
  IsConditionId: boolean = false,
): Promise<Packet<boolean>> =>
  deleteRequest(`/v1/coveredCallOrder/deleteOpenOrder`, { ids, IsConditionId });

export const getCCOrderBrokerOrderTable = (
  searchParam: {
    id?: number;
    ids?: number[];
    conditionId?: number;
  },
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<any>> =>
  getTable(
    `/v1/coveredCallOrder/BrokerOrderTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getCCOrderChancesTable = (
  searchParam: {
    id?: number;
    ids?: number[];
    conditionId?: number;
  },
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable(
    `/v1/coveredCallOrder/orderChancesTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getCCOrderImbalanceChecksTable = (
  searchParam: { id?: number; ids?: number[]; conditionId?: number },
  offset: number | null,
  pageSize: number | null,
): Promise<Report<any>> =>
  getTable("/v1/coveredCallOrder/ImbalanceChecksTable", searchParam, offset, pageSize);

export const getCCOrderBrokerTradeTable = (
  searchParam: {
    id?: number;
    ids?: number[];
  },
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<any>> =>
  getTable(
    `/v1/coveredCallOrder/BrokerTradeTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getCCOrderTradeChancesTable = (
  searchParam: {
    id: number;
  },
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable(
    `/v1/coveredCallOrder/tradeChancesTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getCCOrderTradeTable = (
  searchParam: ICCOrderSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<ICCOrderTableReport>> =>
  getTable<ICCOrderSearchParams, ICCOrderTableReport>(
    `/v1/coveredCallOrder/tradeTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getCCOrderErrorTable = (
  searchParam: ICCOrderSearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
): Promise<Report<any>> =>
  getTable<ICCOrderSearchParams, any>(
    `/v1/coveredCallOrder/errorTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
  );

export const getCCOrderBook = (
  body: ICCOrderbookSearchParams,
  abortSignal: AbortSignal,
): Promise<ICCOrderbooksResp> =>
  postRequest("/v1/coveredCallOrder/GetOrderBook", body, {
    signal: abortSignal,
  });

export const getPairTypeDropdown = (): Promise<DropdownItem[]> =>
  getRequest("v1/coveredcall/getPairTypeDropdown");

export const getCCOrders = (
  order: IGetCCOrder,
): Promise<ICCOrderAddOrderFormValues[]> =>
  postRequest("/v1/CoveredCallOrder/GetOrder", order);

export const getCCOrderUsers = (profileIds: number[]): Promise<User[]> =>
  postRequest("/v1/CoveredCallOrder/GetUsers", profileIds);

export const getCCOrderRules = (): Promise<ICCOrderRules> =>
  getRequest("/v1/CoveredCallOrder/CoveredCallRules");

export const getCCOrderCriterionTypes = (): Promise<ICCOrderCriterionTypes> =>
  getRequest("/v1/CoveredCallOrder/GetCriterionTypes");

export const getCCOrderImbalanceConfig = (
  axiosOptions?: AxiosRequestConfig,
): Promise<ICCOrderImbalanceConfig> =>
  getRequest("/v1/CoveredCallOrder/GetImbalanceConfig", axiosOptions);

export const setCCOrderImbalanceConfig = (
  rows: ICCOrderImbalanceRule[],
): Promise<Packet<boolean>> =>
  putRequest("/v1/CoveredCallOrder/SetImbalanceConfig", { rows });

export const getCoveredCallSendPriorityConfig = (
  orderType: DeltaHedgeOrderType,
): Promise<ICoveredCallSendPriorityView> =>
  getRequest(`/v1/CoveredCallOrder/SendPriorityConfig?orderType=${orderType}`);

export const getCoveredCallSendPriorityTable = (
  searchParam: ICoveredCallSendPrioritySearchParams,
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<ICoveredCallSendPriorityTableRow>> =>
  getTable<
    ICoveredCallSendPrioritySearchParams,
    ICoveredCallSendPriorityTableRow
  >(
    "/v1/CoveredCallOrder/SendPriorityTable",
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );

export const getCoveredCallSendPriorityAccounts = (): Promise<
  ICoveredCallSendPriorityAccount[]
> => getRequest("/v1/CoveredCallOrder/SendPriorityAccounts");

export const setCoveredCallSendPriorityMode = (
  orderType: DeltaHedgeOrderType,
  mode: CoveredCallSendPriorityMode,
): Promise<Packet<boolean>> =>
  putRequest("/v1/CoveredCallOrder/SendPriorityMode", { orderType, mode });

export const setCoveredCallManualSendPriorities = (
  orderType: DeltaHedgeOrderType,
  items: ICoveredCallManualPriorityItem[],
  otherPriority: number,
): Promise<Packet<boolean>> =>
  putRequest("/v1/CoveredCallOrder/ManualSendPriorities", {
    orderType,
    items,
    otherPriority,
  });

export const refreshCoveredCallSendPriorities = (
  orderType: DeltaHedgeOrderType,
  mode: CoveredCallSendPriorityMode,
): Promise<Packet<boolean>> =>
  postRequest(
    `/v1/CoveredCallOrder/RefreshSendPriorities?orderType=${orderType}&mode=${mode}`,
    {},
  );

export const setCCOrderCriterionTypeDefaults = (
  formValues: Pick<ICCOrderCriterionTypes, "defaults">,
): Promise<Packet<boolean>> =>
  putRequest("/v1/CoveredCallOrder/SetCriterionTypeDefaults", formValues);

export const editCCOrder = (
  formValues: ICCOrderEditValues,
): Promise<Packet<boolean>> =>
  putRequest("/v1/CoveredCallOrder/Edit", formValues);

export const updateCriterion = (
  formValues: IUpdateCriterionDTO,
  riskProfileId?: string,
): Promise<Packet<boolean>> =>
  putRequest(
    `/v1/CoveredCallOrder/UpdateCriterion?riskProfileId=${riskProfileId || ""}`,
    formValues,
  );

export const getCCOrderOrderChart = (id: number): Promise<Graph_Dto[]> =>
  getRequest(`/v1/CoveredCallOrder/orderChart?id=${id}`);

export const getCCOrderEvents = (
  algorithmOrderId: number,
  conditionId: number,
  windowLen: number = 5,
  axiosOptions?: AxiosRequestConfig,
): Promise<IShekarOrderMetricEventsResponse> =>
  getRequest(
    `/v1/CoveredCallOrder/Events?algorithm_order_id=${algorithmOrderId}&condition_id=${conditionId}&windowLen=${windowLen}`,
    axiosOptions,
  );

export const getCCOrderAggregationChart = (
  conditionId: number,
): Promise<Graph_Dto[]> =>
  getRequest(`/v1/CoveredCallOrder/orderChart?conditionId=${conditionId}`);

export const getCCOrderDetailsOrderTable = (
  searchParam: {
    id?: number;
    conditionId?: number;
  },
  offset: number | null,
  pageSize: number | null,
  sortingType?: string,
  additional?: DropdownItem,
  axiosOptions?: AxiosRequestConfig,
): Promise<Report<any>> =>
  getTable(
    `/v1/CoveredCallOrder/OrderTable`,
    searchParam,
    offset,
    pageSize,
    sortingType,
    additional,
    axiosOptions,
  );
